import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const ToastContext = createContext(null);

let idSeq = 0;

/**
 * Sistema de notificaciones (toasts) propio, sin dependencias externas.
 * Reemplaza los alert() nativos. Los toasts se muestran fijos arriba a la
 * derecha, fuera del flujo de la página (no tapan formularios ni el canvas
 * de firma).
 */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const remove = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const push = useCallback(
        (message, type = "info", duration = 4000) => {
            const id = ++idSeq;
            setToasts((prev) => [...prev, { id, message, type }]);
            if (duration > 0) {
                setTimeout(() => remove(id), duration);
            }
            return id;
        },
        [remove]
    );

    const api = useMemo(
        () => ({
            show: (msg, opts) => push(msg, opts?.type || "info", opts?.duration),
            success: (msg, opts) => push(msg, "success", opts?.duration),
            error: (msg, opts) => push(msg, "error", opts?.duration),
            info: (msg, opts) => push(msg, "info", opts?.duration),
            dismiss: remove,
        }),
        [push, remove]
    );

    return (
        <ToastContext.Provider value={api}>
            {children}
            <div style={styles.container} aria-live="polite" aria-atomic="true">
                {toasts.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="status"
                        aria-label="Cerrar notificación"
                        onClick={() => remove(t.id)}
                        style={{ ...styles.toast, ...(styles[t.type] || styles.info) }}
                    >
                        {t.message}
                    </button>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        // Fallback seguro si el provider no está montado (evita romper el render).
        return { show: () => {}, success: () => {}, error: () => {}, info: () => {}, dismiss: () => {} };
    }
    return ctx;
}

const styles = {
    container: {
        position: "fixed", top: 16, right: 16, zIndex: 9999,
        display: "flex", flexDirection: "column", gap: 8, maxWidth: "90vw",
    },
    toast: {
        minWidth: 240, maxWidth: 360, padding: "12px 16px", borderRadius: 8,
        color: "#fff", fontFamily: "Arial,sans-serif", fontSize: 14,
        boxShadow: "0 4px 14px rgba(0,0,0,0.18)", cursor: "pointer",
        border: "none", textAlign: "left", width: "100%", appearance: "none",
    },
    success: { background: "#1a6b3a" },
    error: { background: "#c0392b" },
    info: { background: "#334155" },
};
